import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import type { CollapseListener } from '@src/orders/CollapseListener';
import { EditOrderCollapsible } from '@src/orders/EditOrderCollapsible';
import type { FormEntries } from '@src/orders/FormEntries';
import { useSectionError } from '@src/orders/hooks/useSectionError';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import { ControllerFormTextInput } from '@src/orders/PermitFormSections/ControllerFormTextInput';
import {
  CarrierContactNameField,
  CompanyEmailField,
  CompanyFaxField,
  companyInformationFields,
  CompanyNameField,
  CompanyPhoneField,
  DoingBusinessAsField,
} from '@src/permitField/CompanyInformationSectionFields';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Props = { fields: string[] } & CollapseListener;

export const CompanyInformationSection = (props: Props) => {
  const { t } = useTranslation();

  const { control } = useFormContext<FormEntries<typeof props.fields>>();

  const { sectionInError } = useSectionError(
    Object.keys(companyInformationFields),
  );

  if (props.fields.length <= 0) {
    return null;
  }

  return (
    <EditOrderCollapsible
      title={t('companyInformation')}
      collapseEventName={props.collapseEventName}
      uncollapseEventName={props.uncollapseEventName}
      inError={sectionInError}
    >
      <Stack gap={theme.spacing['3']}>
        <Stack direction="row" gap={theme.spacing['3']}>
          <ConditionalField
            fields={props.fields}
            conditions={CompanyNameField.fieldName}
          >
            <ControllerFormTextInput
              disabled
              control={control}
              fieldName={CompanyNameField.fieldName}
            />
          </ConditionalField>

          <ConditionalField
            fields={props.fields}
            conditions={DoingBusinessAsField.fieldName}
          >
            <ControllerFormTextInput
              disabled
              control={control}
              fieldName={DoingBusinessAsField.fieldName}
            />
          </ConditionalField>
        </Stack>
        <Stack direction="row" gap={theme.spacing['3']}>
          <ConditionalField
            fields={props.fields}
            conditions={CompanyPhoneField.fieldName}
          >
            <ControllerFormTextInput
              disabled
              control={control}
              fieldName={CompanyPhoneField.fieldName}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={CompanyFaxField.fieldName}
          >
            <ControllerFormTextInput
              disabled
              control={control}
              fieldName={CompanyFaxField.fieldName}
            />
          </ConditionalField>
        </Stack>

        <Stack direction="row" gap={theme.spacing['3']}>
          <ConditionalField
            fields={props.fields}
            conditions={CarrierContactNameField.fieldName}
          >
            <ControllerFormTextInput
              disabled
              control={control}
              fieldName={CarrierContactNameField.fieldName}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={CompanyEmailField.fieldName}
          >
            <ControllerFormTextInput
              disabled
              control={control}
              fieldName={CompanyEmailField.fieldName}
            />
          </ConditionalField>
        </Stack>
      </Stack>
    </EditOrderCollapsible>
  );
};
